import { useEffect, useState } from "react";
import { motion } from "@/lib/motion";
import {
    ArrowUpRight,
    HeartPulse,
    ShieldCheck,
    Users,
    Network,
    UserCog,
    Activity,
    Lock,
    Gauge,
    Eye,
    FileCheck,
    ServerCog,
    Cloud,
    Compass,
    DatabaseBackup,
    Search,
    Link2,
    ShieldPlus,
    RefreshCw,
    ClipboardCheck,
    FileText,
    GraduationCap,
    Siren,
    KeyRound,
    FolderCog,
    CalendarClock,
    } from "lucide-react";
import "@/App.css";
import { ThemeProvider } from "@/context/ThemeContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Analytics } from "@/components/Analytics";
import { Overline } from "@/components/Reveal";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { getHealthcarePage } from "@/lib/sanityQueries";
import { peek } from "@/lib/sanity";
import { skipIntro } from "@/lib/prerender";

const ease = [0.22, 1, 0.36, 1];
const ext = (cta) =>
    cta?.openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {};

// Mount-triggered reveal (same fade/rise motion as the shared Reveal) so every
// Healthcare section is visible in normal document flow regardless of scroll
// position or full-page capture — the shared whileInView variant left
// below-the-fold content at opacity:0 until scrolled into view.
const Reveal = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={skipIntro() ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay, ease }}
        className={className}
    >
        {children}
    </motion.div>
);

// Frontend-owned icons, mapped by position.
const CHALLENGE_ICONS = [HeartPulse, Users, Network, UserCog, Activity];
const OUTCOME_ICONS = [Lock, Gauge, Eye, FileCheck];
const SERVICE_ICONS = [ServerCog, ShieldCheck, Cloud, Compass, DatabaseBackup];
const EASI_ICONS = [Search, Link2, ShieldPlus, RefreshCw];
const SAFEGUARD_ICONS = [
    ClipboardCheck,
    FileText,
    GraduationCap,
    Siren,
    KeyRound,
    FolderCog,
    FolderCog,
    CalendarClock,
];

// Fallbacks (used only if Sanity is unavailable).
const FB = {
    heroEyebrow: "Purpose-Built for Healthcare Organizations",
    heroHeadline: "Secure Technology. Reliable Operations. Stronger Compliance Support.",
    heroSupportingMessage:
        "Healthcare Secure™ combines managed IT, cybersecurity, cloud services, and practical support for HIPAA security safeguards—designed around the needs of growing healthcare practices.",
    heroPrimaryCta: { label: "Schedule a Conversation", url: "#", openInNewTab: false },
    heroSecondaryCta: { label: "Explore Healthcare Secure™ Services", url: "#services", openInNewTab: false },
    challengeHeadline: "Healthcare Technology Carries Higher Stakes",
    challengeIntro:
        "Healthcare practices depend on technology for scheduling, communication, records, billing, devices, and daily operations.",
    healthcareChallenges: [
        { _key: "c1", title: "Sensitive Patient Information", description: "Healthcare organizations must protect sensitive information across email, cloud platforms, endpoints, shared files, applications, and third-party systems." },
        { _key: "c2", title: "Limited Internal IT Resources", description: "Smaller practices often face complex technology and security responsibilities without dedicated internal IT or cybersecurity teams." },
        { _key: "c3", title: "Vendor & System Complexity", description: "Clinical applications, cloud platforms, devices, internet services, vendors, and support providers can create fragmented responsibility and limited visibility." },
        { _key: "c4", title: "Access & Workforce Changes", description: "Onboarding, offboarding, shared accounts, permissions, and administrator access require consistent processes to reduce unnecessary exposure." },
        { _key: "c5", title: "Operational Continuity", description: "Technology disruptions can affect scheduling, communication, records access, billing, and patient-facing operations." },
    ],
    outcomesHeadline: "Technology That Supports Better Practice Operations",
    healthcareOutcomes: [
        { _key: "o1", title: "Stronger Security", description: "Improve protection across identities, endpoints, email, cloud applications, and business systems." },
        { _key: "o2", title: "Greater Reliability", description: "Reduce technology disruption and create more consistent support for daily practice operations." },
        { _key: "o3", title: "Improved Visibility", description: "Create clearer oversight of devices, accounts, vendors, risks, and technology responsibilities." },
        { _key: "o4", title: "Stronger Compliance Support", description: "Support the administrative and technical safeguards that contribute to a stronger HIPAA security program." },
    ],
    easiHeadline: "A Practical Framework for Stronger Healthcare Technology",
    easiIntro:
        "The E.A.S.I.™ methodology gives Healthcare Secure™ a structured approach for evaluating risk, improving safeguards, strengthening operations, and supporting continuous improvement.",
    easiSteps: [
        { _key: "e", letter: "E", title: "Evaluate", description: "Understand the practice environment, technology risks, operational gaps, and areas requiring greater visibility." },
        { _key: "a", letter: "A", title: "Align", description: "Connect technology priorities, security safeguards, operational needs, and compliance responsibilities." },
        { _key: "s", letter: "S", title: "Secure", description: "Strengthen identities, endpoints, email, data, cloud applications, backups, and other critical technology layers." },
        { _key: "i", letter: "I", title: "Improve", description: "Continuously review systems, processes, risks, and safeguards as the practice and technology environment evolve." },
    ],
    servicesHeadline: "Healthcare Technology Services Built Around Practice Needs",
    servicesIntro:
        "Healthcare Secure™ brings together managed IT, cybersecurity, cloud infrastructure, backup, and strategic guidance in a service model designed for healthcare organizations.",
    featuredServices: [
        { _id: "s1", name: "Managed IT & Operational Excellence", shortDescription: "Proactive monitoring, support, and operational processes that keep your practice moving." },
        { _id: "s2", name: "Cybersecurity & Risk Protection", shortDescription: "Managed safeguards across identities, endpoints, email, networks, and backups." },
        { _id: "s3", name: "Cloud, Infrastructure & Scalability", shortDescription: "Secure cloud and resilient infrastructure for collaboration, continuity, and growth." },
        { _id: "s4", name: "IT Strategy, Advisory & vCIO Services", shortDescription: "Strategic guidance connecting IT decisions to business goals and budget." },
        { _id: "s5", name: "Business Continuity & Resilience", shortDescription: "Continuity ownership that keeps your practice operational, whatever happens." },
    ],
    safeguardsHeadline: "Support for HIPAA Administrative & Technical Safeguards",
    safeguardsMessage:
        "Healthcare Secure™ helps practices strengthen the technology, security processes, documentation, and operational controls that support a broader HIPAA security and compliance program.",
    safeguards: [
        "Security risk assessment facilitation",
        "Security policy and documentation support",
        "Security awareness training coordination",
        "Incident response planning assistance",
        "Workforce access and security procedures",
        "Vendor and BAA tracking support",
        "HIPAA documentation organization",
        "Ongoing security and technology reviews",
    ],
    featuredFaqs: [
        { _id: "f1", question: "What is Healthcare Secure™?", answer: "Healthcare Secure™ is EasiLinked’s technology and security offering designed specifically for small and growing healthcare practices and healthcare-adjacent organizations." },
        { _id: "f2", question: "Does Healthcare Secure™ guarantee HIPAA compliance?", answer: "No provider can guarantee an organization’s overall HIPAA compliance. EasiLinked supports healthcare organizations with technology safeguards, security practices, risk-management processes, documentation support, and other services designed to strengthen their HIPAA security and compliance programs." },
        { _id: "f3", question: "Why do smaller healthcare practices need dedicated cybersecurity?", answer: "Smaller healthcare organizations manage sensitive information and depend heavily on technology, but often have limited internal security resources." },
        { _id: "f4", question: "Does EasiLinked support cloud-first organizations?", answer: "Yes. EasiLinked supports cloud-first environments, including Google Workspace administration, cloud migrations, backup and recovery, identity management, collaboration, and security." },
    ],
    finalCtaHeadline: "Technology Should Support Patient Care—Not Create More Risk.",
    finalCtaMessage:
        "Let’s talk about your current environment, security concerns, compliance responsibilities, and what stronger technology support could look like for your practice.",
    finalCta: { label: "Schedule a Conversation", url: "#", openInNewTab: false },
};

const useHealthcareSeo = (seo) => {
    useEffect(() => {
        if (!seo) return;
        const prevTitle = document.title;
        if (seo.metaTitle) document.title = seo.metaTitle;

        const upsertMeta = (attr, key, value) => {
            if (!value) return null;
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            const created = !el;
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute("content", value);
            return { el, created };
        };

        const desc = upsertMeta("name", "description", seo.metaDescription);
        const robots = seo.noIndex ? upsertMeta("name", "robots", "noindex, nofollow") : null;

        let canonical = null;
        if (seo.canonicalUrl) {
            canonical = document.head.querySelector('link[rel="canonical"]') || null;
            if (!canonical) {
                canonical = document.createElement("link");
                canonical.setAttribute("rel", "canonical");
                document.head.appendChild(canonical);
            }
            canonical.setAttribute("href", seo.canonicalUrl);
        }

        return () => {
            document.title = prevTitle;
            if (robots?.created) robots.el.remove();
            if (canonical) canonical.remove();
            // description meta is left in place (commonly present site-wide)
        };
    }, [seo]);
};

const CtaLink = ({ cta, fallbackLabel, variant = "primary", testid }) => {
    const label = cta?.label || fallbackLabel;
    const url = cta?.url || "#";
    const base =
        "group inline-flex items-center gap-2 rounded-control px-7 py-3.5 text-sm font-semibold transition-colors duration-150";
    const styles =
        variant === "primary"
            ? "bg-brand-accent text-brand-on-accent"
            : "border-[1.5px] border-brand-control-border text-brand-text hover:bg-brand-surface2";
    return (
        <a href={url} {...ext(cta)} data-testid={testid} className={`${base} ${styles}`}>
            {label}
            <ArrowUpRight className="h-4 w-4 transition-colors duration-150" />
        </a>
    );
};

const HealthcareContent = () => {
    const [data, setData] = useState(() => peek(getHealthcarePage));
    useEffect(() => {
        let active = true;
        getHealthcarePage()
            .then((res) => {
                if (active && res) setData(res);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    useHealthcareSeo(data?.seo);

    const d = data || {};
    const challenges = d.healthcareChallenges?.length ? d.healthcareChallenges : FB.healthcareChallenges;
    const outcomes = d.healthcareOutcomes?.length ? d.healthcareOutcomes : FB.healthcareOutcomes;
    const easiSteps = d.easiSteps?.length ? d.easiSteps : FB.easiSteps;
    const svcs = d.featuredServices?.length ? d.featuredServices : FB.featuredServices;
    const safeguards = d.safeguards?.length ? d.safeguards : FB.safeguards;
    const faqs = d.featuredFaqs?.length ? d.featuredFaqs : FB.featuredFaqs;

    return (
        <div className="min-h-screen bg-brand-bg font-body text-brand-text antialiased">
            <Navbar />
            <main>
                {/* 1. HERO */}
                <section
                    id="top"
                    data-testid="healthcare-hero"
                    className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-24"
                >
                    <div className="pointer-events-none absolute inset-0 el-grid-bg" aria-hidden />
                    <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
                        <Reveal>
                            <span className="inline-flex items-center">
                                <Overline>{d.heroEyebrow || FB.heroEyebrow}</Overline>
                            </span>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h1
                                data-testid="healthcare-hero-heading"
                                className="mt-7 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-6xl"
                            >
                                {d.heroHeadline || FB.heroHeadline}
                            </h1>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-brand-muted sm:text-lg">
                                {d.heroSupportingMessage || FB.heroSupportingMessage}
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                                <CtaLink
                                    cta={d.heroPrimaryCta}
                                    fallbackLabel={FB.heroPrimaryCta.label}
                                    variant="primary"
                                    testid="healthcare-hero-primary-cta"
                                />
                                <CtaLink
                                    cta={d.heroSecondaryCta}
                                    fallbackLabel={FB.heroSecondaryCta.label}
                                    variant="secondary"
                                    testid="healthcare-hero-secondary-cta"
                                />
                            </div>
                        </Reveal>
                    </div>
                </section>

                {/* 2. HEALTHCARE CHALLENGES */}
                <section
                    data-testid="healthcare-challenges"
                    className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28"
                >
                    <div className="max-w-2xl">
                        <Reveal>
                            <Overline>The Healthcare Reality</Overline>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                                {d.challengeHeadline || FB.challengeHeadline}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-base leading-relaxed text-brand-muted">
                                {d.challengeIntro || FB.challengeIntro}
                            </p>
                        </Reveal>
                    </div>
                    <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {challenges.map((c, i) => {
                            const Icon = CHALLENGE_ICONS[i] || CHALLENGE_ICONS[CHALLENGE_ICONS.length - 1];
                            return (
                                <Reveal key={c._key || i} delay={(i % 3) * 0.06}>
                                    <motion.div
                                        whileHover={{ y: -4 }}
                                        transition={{ duration: 0.3 }}
                                        className="h-full rounded-2xl border border-brand-line bg-brand-surface p-6 transition-colors duration-150 hover:border-brand-accent/40"
                                    >
                                        <span className="grid h-12 w-12 place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent">
                                            <Icon className="h-5 w-5" />
                                        </span>
                                        <h3 className="mt-5 font-heading text-lg font-bold tracking-tight text-brand-text">
                                            {c.title}
                                        </h3>
                                        <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                            {c.description}
                                        </p>
                                    </motion.div>
                                </Reveal>
                            );
                        })}
                    </div>
                </section>

                {/* 3. HEALTHCARE OUTCOMES */}
                <section
                    data-testid="healthcare-outcomes"
                    className="border-y border-brand-line bg-brand-surface py-24 sm:py-28"
                >
                    <div className="mx-auto max-w-7xl px-5 sm:px-8">
                        <Reveal>
                            <Overline>Outcomes</Overline>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-5 max-w-2xl font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                                {d.outcomesHeadline || FB.outcomesHeadline}
                            </h2>
                        </Reveal>
                        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                            {outcomes.map((o, i) => {
                                const Icon = OUTCOME_ICONS[i] || OUTCOME_ICONS[OUTCOME_ICONS.length - 1];
                                return (
                                    <Reveal key={o._key || i} delay={i * 0.06}>
                                        <div className="h-full rounded-2xl border border-brand-line bg-brand-bg p-6">
                                            <div className="flex items-center justify-between">
                                                <span className="font-heading text-3xl font-extrabold tracking-tight text-brand-accent">
                                                    {`0${i + 1}`}
                                                </span>
                                                <Icon className="h-5 w-5 text-brand-muted" />
                                            </div>
                                            <h3 className="mt-5 font-heading text-lg font-bold tracking-tight text-brand-text">
                                                {o.title}
                                            </h3>
                                            <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                                {o.description}
                                            </p>
                                        </div>
                                    </Reveal>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* 4. E.A.S.I.™ METHODOLOGY */}
                <section
                    data-testid="healthcare-easi"
                    className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28"
                >
                    <div className="max-w-2xl">
                        <Reveal>
                            <Overline>The E.A.S.I.™ Methodology</Overline>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                                {d.easiHeadline || FB.easiHeadline}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-base leading-relaxed text-brand-muted">
                                {d.easiIntro || FB.easiIntro}
                            </p>
                        </Reveal>
                    </div>
                    <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {easiSteps.map((s, i) => {
                            const Icon = EASI_ICONS[i] || EASI_ICONS[EASI_ICONS.length - 1];
                            return (
                                <Reveal key={s._key || i} delay={i * 0.06}>
                                    <div
                                        data-testid={`healthcare-easi-step-${i}`}
                                        className="h-full rounded-2xl border border-brand-line bg-brand-surface p-6"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="grid h-14 w-14 place-items-center rounded-2xl border border-brand-line bg-brand-bg font-heading text-2xl font-extrabold tracking-tight text-brand-accent">
                                                {s.letter}
                                            </span>
                                            <Icon className="h-5 w-5 text-brand-muted" />
                                        </div>
                                        <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-brand-text">
                                            {s.title}
                                        </h3>
                                        <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                            {s.description}
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </section>

                {/* 5. HEALTHCARE SERVICES */}
                <section
                    id="services"
                    data-testid="healthcare-services"
                    className="border-y border-brand-line bg-brand-surface py-24 sm:py-28"
                >
                    <div className="mx-auto max-w-7xl px-5 sm:px-8">
                        <div className="mb-12 max-w-2xl">
                            <Reveal>
                                <Overline>Capabilities</Overline>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                                    {d.servicesHeadline || FB.servicesHeadline}
                                </h2>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <p className="mt-6 text-base leading-relaxed text-brand-muted">
                                    {d.servicesIntro || FB.servicesIntro}
                                </p>
                            </Reveal>
                        </div>
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                            {svcs.map((s, i) => {
                                const Icon = SERVICE_ICONS[i] || SERVICE_ICONS[SERVICE_ICONS.length - 1];
                                return (
                                    <Reveal key={s._id || i} delay={(i % 3) * 0.06}>
                                        <motion.div
                                            whileHover={{ y: -6 }}
                                            transition={{ duration: 0.35, ease }}
                                            data-testid={`healthcare-service-card-${i}`}
                                            className="group h-full rounded-3xl border border-brand-line bg-brand-bg p-7 transition-colors duration-150 hover:border-brand-accent/40"
                                        >
                                            <div className="flex items-start justify-between">
                                                <span className="grid h-14 w-14 place-items-center rounded-2xl border border-brand-line bg-brand-surface text-brand-accent transition-colors duration-150 group-hover:bg-brand-accent group-hover:text-brand-on-accent">
                                                    <Icon className="h-6 w-6" />
                                                </span>
                                                <ArrowUpRight className="h-5 w-5 text-brand-muted opacity-0 transition-colors duration-150 group-hover:opacity-100" />
                                            </div>
                                            <h3 className="mt-6 font-heading text-xl font-bold tracking-tight text-brand-text">
                                                {s.name}
                                            </h3>
                                            <p className="mt-3 text-sm leading-relaxed text-brand-muted">
                                                {s.shortDescription}
                                            </p>
                                        </motion.div>
                                    </Reveal>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* 6. HIPAA SAFEGUARDS SUPPORT */}
                <section
                    data-testid="healthcare-safeguards"
                    className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28"
                >
                    <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:items-start">
                        <div className="lg:col-span-4">
                            <Reveal>
                                <Overline>Compliance Support</Overline>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                                    {d.safeguardsHeadline || FB.safeguardsHeadline}
                                </h2>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <p className="mt-6 text-base leading-relaxed text-brand-muted">
                                    {d.safeguardsMessage || FB.safeguardsMessage}
                                </p>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-8">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {safeguards.map((s, i) => {
                                    const Icon = SAFEGUARD_ICONS[i] || SAFEGUARD_ICONS[SAFEGUARD_ICONS.length - 1];
                                    return (
                                        <Reveal key={i} delay={(i % 2) * 0.06}>
                                            <div
                                                data-testid={`healthcare-safeguard-${i}`}
                                                className="flex h-full items-center gap-4 rounded-2xl border border-brand-line bg-brand-surface p-5"
                                            >
                                                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent">
                                                    <Icon className="h-5 w-5" />
                                                </span>
                                                <p className="text-sm font-medium leading-snug text-brand-text">
                                                    {s}
                                                </p>
                                            </div>
                                        </Reveal>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                {/* 8. FAQs */}
                <section
                    data-testid="healthcare-faqs"
                    className="mx-auto max-w-3xl px-5 py-24 sm:px-8 sm:py-28"
                >
                    <Reveal>
                        <Overline>FAQ</Overline>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                            Common Questions
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Accordion type="single" collapsible className="mt-10 w-full">
                            {faqs.map((f, i) => (
                                <AccordionItem
                                    key={f._id || i}
                                    value={`faq-${i}`}
                                    data-testid={`healthcare-faq-${i}`}
                                    className="border-brand-line"
                                >
                                    <AccordionTrigger className="text-left font-heading text-lg font-bold tracking-tight text-brand-text hover:no-underline">
                                        {f.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-sm leading-relaxed text-brand-muted">
                                        {f.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </section>

                {/* 9. CLOSING CTA */}
                <section
                    data-testid="healthcare-closing-cta"
                    className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28"
                >
                    <div className="relative overflow-hidden rounded-card border border-brand-line bg-brand-surface px-6 py-20 text-center sm:px-16 sm:py-24">
                        <div className="pointer-events-none absolute inset-0 el-grid-bg opacity-40" aria-hidden />
                        <Reveal>
                            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-accent">
                                Start the conversation
                            </span>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mx-auto mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.0] tracking-tight text-brand-text sm:text-5xl">
                                {d.finalCtaHeadline || FB.finalCtaHeadline}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-brand-muted">
                                {d.finalCtaMessage || FB.finalCtaMessage}
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="mt-10 flex justify-center">
                                <CtaLink
                                    cta={d.finalCta}
                                    fallbackLabel={FB.finalCta.label}
                                    variant="primary"
                                    testid="healthcare-closing-cta-button"
                                />
                            </div>
                        </Reveal>
                    </div>
                </section>
            </main>
            <Footer />
            <Analytics />
        </div>
    );
};

export default function HealthcarePage() {
    return (
        <ThemeProvider>
            <HealthcareContent />
        </ThemeProvider>
    );
}
